import React, { useState } from 'react';
import { Form, Row, Col, Input, Button } from 'reactstrap';
import { ModalButtonBuilder, RegisteredInputsBuilder, ToastBuilder, ButtonBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { capitalizeFirstLetter, buildSelectOptionsFromArray, parseJSONSafe } from '../../../../../../../../../GlobalComponents/Helpers';
import { useForm } from 'react-hook-form';

const MappingConditionsBuilder = ({
    schema,
    getCurrentConditions,
    onApply,
    readOnly,
    getKeyResources,
    getSetupResources,
    getSuppDataResources,
    getDestinationResources,
    destinationKey,
}) => {

    const { register, handleSubmit, errors = {}, control, setValue, watch } = useForm();
    const [customFields, setCustomFields] = useState([]);

    if (!schema || typeof schema !== 'object' || Object.keys(schema).length === 0) return null;

    const safeArray = (fn, ...args) => { try { const r = fn?.(...args); return Array.isArray(r) ? r : []; } catch { return []; } };

    const getSourceOptions = (source, fieldKey) => {
        switch (source) {
            case 'key_resources': return safeArray(getKeyResources);
            case 'setup_resources': return safeArray(getSetupResources);
            case 'supplementary_resources': return safeArray(getSuppDataResources);
            case 'destination_resources': {
                // Find which field this depends on via schema dependsOn
                const field = schema[fieldKey];
                const dependsOnKey = field?.dependsOn;
                if (dependsOnKey) {
                    const selectedSource = watch(dependsOnKey);
                    if (selectedSource) return safeArray(getDestinationResources, selectedSource);
                    return [];
                }
                return safeArray(getDestinationResources);
            }
            default: return [];
        }
    };

    const fieldDefaults = { size: 11, labelSize: 5, inputSize: 7 };

    const buildFieldInput = (key, field) => {
        if (!field || typeof field !== 'object') return null;
        const label = field.label || capitalizeFirstLetter(key.replace(/_/g, ' '));

        if (field.source) {
            const options = getSourceOptions(field.source, key);
            // If field depends on another and that field has no value yet, show disabled hint
            if (field.dependsOn) {
                const dependsOnValue = watch(field.dependsOn);
                if (!dependsOnValue) {
                    return {
                        ...fieldDefaults, label, name: key, type: 'reactselect',
                        options: [{ label: `Select ${schema[field.dependsOn]?.label || field.dependsOn} first`, value: '' }],
                        disabled: true,
                    };
                }
            }
            return {
                ...fieldDefaults, label, name: key, type: 'reactselect',
                options: [{ label: 'Select...', value: '' }, ...options],
            };
        }

        if (Array.isArray(field.options) && field.options.length > 0) {
            return {
                ...fieldDefaults, label, name: key, type: 'reactselect',
                options: buildSelectOptionsFromArray(field.options, { labelIsValue: true }),
            };
        }

        if (field.type === 'boolean') {
            return {
                ...fieldDefaults, label, name: key, type: 'reactselect',
                options: [{ label: 'True', value: true }, { label: 'False', value: false }],
            };
        }

        if (field.type === 'number') {
            return { ...fieldDefaults, label, name: key, type: 'number' };
        }

        return { ...fieldDefaults, label, name: key };
    };

    const parseCurrentConditions = () => {
        try {
            const currentConditions = typeof getCurrentConditions === 'function' ? getCurrentConditions() : getCurrentConditions;
            if (!currentConditions) return {};
            const cleaned = typeof currentConditions === 'string' ? currentConditions.replace(/^#/, '').trim() : currentConditions;
            const parsed = parseJSONSafe(cleaned);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch { return {}; }
    };

    const loadConditionsToForm = () => {
        const parsed = parseCurrentConditions();
        const schemaKeys = new Set(Object.keys(schema));

        Object.entries(schema).forEach(([key, field]) => {
            if (!field || typeof field !== 'object') return;
            let value = parsed[key] ?? field.default ?? '';
            if (field.autoFromDestinationKey && !parsed[key] && destinationKey) {
                value = destinationKey.toLowerCase().endsWith('/name');
            }
            // Treat 0 as empty for source/select fields to avoid "Unknown: 0"
            if (field.source && (value === 0 || value === '0')) value = '';
            setValue(key, value);
        });

        // Load extra keys not in schema as custom fields
        const extras = Object.entries(parsed)
            .filter(([key]) => !schemaKeys.has(key) && !Object.values(schema).some(f => f?.companionFields && Object.values(f.companionFields).includes(key)))
            .map(([key, value]) => ({ key, value: typeof value === 'object' ? JSON.stringify(value) : String(value) }));
        setCustomFields(extras.length > 0 ? extras : []);
    };

    const addCustomField = () => setCustomFields(prev => [...prev, { key: '', value: '' }]);

    const updateCustomField = (index, field, val) => {
        setCustomFields(prev => prev.map((f, i) => i === index ? { ...f, [field]: val } : f));
    };

    const removeCustomField = (index) => setCustomFields(prev => prev.filter((_, i) => i !== index));

    const parseCustomValue = (val) => {
        if (val === 'true') return true;
        if (val === 'false') return false;
        if (val !== '' && !isNaN(val) && !isNaN(parseFloat(val))) return Number(val);
        try { const p = JSON.parse(val); if (typeof p === 'object') return p; } catch {}
        return val;
    };

    const onSubmit = (formData) => {
        try {
            const conditions = {};
            Object.entries(schema).forEach(([key, field]) => {
                if (!field || typeof field !== 'object') return;
                let value = formData[key];
                if (value === undefined || value === '') return;
                if (field.type === 'boolean') value = value === true || value === 'true';
                else if (field.type === 'number') value = Number(value);

                // Handle companion fields from schema
                if (field.source && value && field.companionFields) {
                    const options = getSourceOptions(field.source, key);
                    const match = options.find(o => String(o.value) === String(value));
                    if (match) {
                        Object.entries(field.companionFields).forEach(([type, condKey]) => {
                            if (type === 'name') conditions[condKey] = match.label;
                            if (type === 'id') conditions[condKey] = value;
                        });
                    }
                }

                conditions[key] = value;
            });

            // Merge custom fields
            customFields.forEach(({ key, value }) => {
                if (key && key.trim()) conditions[key.trim()] = parseCustomValue(value);
            });

            onApply?.(conditions);
        } catch (e) { console.warn('[MappingConditionsBuilder] Submit error:', e); }
    };

    // Build inputs inside the render body so watch() triggers re-renders.
    // Sort by optional `order` (unordered fields keep their natural order, last),
    // then bucket by optional `group` into labeled sections. Fields with no group
    // fall into the default section titled "Schema Properties".
    const DEFAULT_GROUP = '__default__';
    const orderedEntries = Object.entries(schema)
        .map(([key, field], i) => ({ key, field, order: field?.order ?? 999, i }))
        .sort((a, b) => (a.order - b.order) || (a.i - b.i));

    const groupInputs = [];      // [{ group, title, inputs: [...] }]
    const groupIndex = {};
    orderedEntries.forEach(({ key, field }) => {
        const input = buildFieldInput(key, field);
        if (!input) return;
        const group = field?.group || DEFAULT_GROUP;
        if (groupIndex[group] === undefined) {
            groupIndex[group] = groupInputs.length;
            const title = group === DEFAULT_GROUP ? 'Schema Properties'
                : capitalizeFirstLetter(group.replace(/_/g, ' '));
            groupInputs.push({ group, title, inputs: [] });
        }
        groupInputs[groupIndex[group]].inputs.push(input);
    });

    const totalInputs = groupInputs.reduce((n, g) => n + g.inputs.length, 0);
    if (totalInputs === 0) return null;

    return (
        <ModalButtonBuilder
            title='Mapping Builder'
            type='button'
            className='btn-info btn-sm'
            modalTitle='Mapping Conditions Builder'
            formId='conditions-builder-form'
            onSubmit={readOnly ? false : 'Apply Conditions'}
            onCancelLabel={readOnly ? 'Close' : 'Cancel'}
            onClick={loadConditionsToForm}
            style={{minWidth:550, fontSize: 13}}
            body={
                <Form id='conditions-builder-form' onSubmit={(e) => { e.stopPropagation(); handleSubmit((data) => {
                    onSubmit(data);
                    ToastBuilder('info', 'Don\'t forget to Submit to save', {}, 5000);
                })(e); }}>
                    {groupInputs.map((g, gi) => (
                        <div key={g.group} className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginTop: gi === 0 ? 0 : 10 }}>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>{g.title}</span>
                            <RegisteredInputsBuilder
                                control={control}
                                registrator={register}
                                registratorErrors={errors}
                                readOnly={readOnly}
                                inputs={g.inputs}
                            />
                        </div>
                    ))}
                    <div className='section-group-accent' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13 }}>Custom Properties</span>
                            {!readOnly && <ButtonBuilder label='+ Add' type='button' className='btn-outline-secondary btn-sm' useSpinner={false} onClick={addCustomField} />}
                        </div>
                        {customFields.map((field, i) => (
                            <Row key={i} className='mb-2 align-items-center'>
                                <Col md={5}>
                                    <Input bsSize='sm' placeholder='Key' value={field.key} readOnly={readOnly}
                                        onChange={(e) => updateCustomField(i, 'key', e.target.value)} />
                                </Col>
                                <Col md={5}>
                                    <Input bsSize='sm' placeholder='Value' value={field.value} readOnly={readOnly}
                                        onChange={(e) => updateCustomField(i, 'value', e.target.value)} />
                                </Col>
                                <Col md={2}>
                                    {!readOnly && <Button size='sm' color='link' className='text-danger p-0' onClick={() => removeCustomField(i)}>✕</Button>}
                                </Col>
                            </Row>
                        ))}
                        {customFields.length === 0 && <p style={{ fontSize: 12, color: '#888', margin: '4px 0' }}>No custom properties</p>}
                    </div>
                </Form>
            }
        />
    );
};

export default MappingConditionsBuilder;
